import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
// FIREBASE MODE (reverted from Supabase)
import {
  getProjectsForUser,
  getFoldersForProject,
  getListsForFolder,
  subscribeToProjects,
  subscribeToTasks,
  subscribeToUsers,
  subscribeToSectionsForList,
  getSectionsForList,
  createDefaultSections,
} from '../services/firestore';

// Stubs for views (not in Firebase firestore.js yet)
const subscribeToViewsForList = () => () => {};
const createDefaultViews = async () => {};
const getUserDefaultView = async () => null;

const TaskContext = createContext();

export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask debe usarse dentro de TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const { user } = useAuth();

  // Estado para proyectos
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Estado para carpetas
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Estado para listas
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [loadingLists, setLoadingLists] = useState(false);

  // Estado para tareas
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedSubtaskPath, setSelectedSubtaskPath] = useState([]); // Array de IDs para navegación de subtareas

  // Estado para "Mis Tareas" (cross-project) — compartido con TaskDrawer
  const [myTasks, setMyTasks] = useState([]);

  // Estado para secciones
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  // Estado para usuarios (colaboradores)
  const [users, setUsers] = useState([]);

  // Estado para UI
  const [loading, setLoading] = useState(true);
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);
  const [activeView, setActiveView] = useState('inicio');
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    estado: null,       // 'planificacion' | 'en-curso' | 'finalizado' | null
    prioridad: null,    // 'baja' | 'media' | 'alta' | null
    asignado: null,     // email string | null
    fechaDesde: null,   // ISO string | null
    fechaHasta: null,   // ISO string | null
  });

  // Suscribirse a usuarios (colaboradores)
  useEffect(() => {
    const unsubscribe = subscribeToUsers((usersData) => {
      setUsers(usersData);
    });
    return () => unsubscribe();
  }, []);

  // Suscribirse a proyectos en tiempo real
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let unsubscribeRef = null;

    subscribeToProjects(user.uid, (projectsData) => {
      console.log('[TaskContext] Projects loaded:', projectsData.length, projectsData.map(p => p.id + ':' + (p.nombre || p.name)));
      setProjects(projectsData);

      // Si no hay proyecto seleccionado pero hay proyectos, seleccionar el primero
      if (projectsData.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsData[0].id);
      }

      setLoading(false);
    }).then(unsubscribe => {
      unsubscribeRef = unsubscribe;
    });

    return () => {
      if (unsubscribeRef && typeof unsubscribeRef === 'function') {
        unsubscribeRef();
      }
    };
  }, [user?.uid]);

  // Cargar carpetas cuando se selecciona un proyecto
  useEffect(() => {
    if (!user?.uid || !selectedProjectId) {
      setFolders([]);
      setSelectedFolderId(null);
      return;
    }

    setLoadingFolders(true);
    getFoldersForProject(user.uid, selectedProjectId)
      .then(foldersData => {
        setFolders(foldersData);

        // Si hay carpetas pero ninguna seleccionada, seleccionar la primera
        if (foldersData.length > 0 && !selectedFolderId) {
          setSelectedFolderId(foldersData[0].id);
        }

        setLoadingFolders(false);
      });
  }, [user?.uid, selectedProjectId]);

  // Cargar listas cuando se selecciona una carpeta
  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId) {
      setLists([]);
      // Don't clear selectedListId here, as it might be set by the URL
      // setSelectedListId(null); 
      return;
    }

    setLoadingLists(true);
    getListsForFolder(user.uid, selectedProjectId, selectedFolderId)
      .then(listsData => {
        setLists(listsData);

        // Si hay listas pero ninguna seleccionada, seleccionar la primera
        if (listsData.length > 0 && !selectedListId) {
          setSelectedListId(listsData[0].id);
        }

        setLoadingLists(false);
      });
  }, [user?.uid, selectedProjectId, selectedFolderId]);

  // Función para recargar carpetas
  const reloadFolders = async () => {
    if (!user?.uid || !selectedProjectId) return;

    setLoadingFolders(true);
    try {
      const foldersData = await getFoldersForProject(user.uid, selectedProjectId);
      setFolders(foldersData);
    } catch (error) {
      console.error('Error al recargar carpetas:', error);
    } finally {
      setLoadingFolders(false);
    }
  };

  // Función para recargar listas
  const reloadLists = async () => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId) return;

    setLoadingLists(true);
    try {
      const listsData = await getListsForFolder(user.uid, selectedProjectId, selectedFolderId);
      setLists(listsData);
    } catch (error) {
      console.error('Error al recargar listas:', error);
    } finally {
      setLoadingLists(false);
    }
  };

  // Suscribirse a tareas en tiempo real
  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
      setTasks([]);
      return;
    }

    let unsubscribeRef = null;

    subscribeToTasks(
      user.uid,
      selectedProjectId,
      selectedFolderId,
      selectedListId,
      (tasksData) => {
        setTasks(tasksData);
      }
    ).then(unsubscribe => {
      unsubscribeRef = unsubscribe;
    });

    return () => {
      if (unsubscribeRef && typeof unsubscribeRef === 'function') {
        unsubscribeRef();
      }
    };
  }, [user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  // Suscribirse a secciones en tiempo real y crear secciones por defecto si no existen
  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
      setSections([]);
      return;
    }

    setLoadingSections(true);
    let unsubscribeRef = null;

    // Primero verificar si hay secciones
    getSectionsForList(user.uid, selectedProjectId, selectedFolderId, selectedListId)
      .then(async (sectionsData) => {
        if (sectionsData.length === 0) {
          // Crear secciones por defecto
          await createDefaultSections(user.uid, selectedProjectId, selectedFolderId, selectedListId);
        }

        // Suscribirse a cambios en tiempo real
        unsubscribeRef = await subscribeToSectionsForList(
          user.uid,
          selectedProjectId,
          selectedFolderId,
          selectedListId,
          (sectionsData) => {
            setSections(sectionsData);
            setLoadingSections(false);
          }
        );
      })
      .catch((error) => {
        console.error('Error al cargar secciones:', error);
        setLoadingSections(false);
      });

    return () => {
      if (unsubscribeRef && typeof unsubscribeRef === 'function') {
        unsubscribeRef();
      }
    };
  }, [user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  // Subscribe to views for the selected list
  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
      setViews([]);
      return;
    }
    let unsubRef = null;
    const init = async () => {
      unsubRef = await subscribeToViewsForList(
        user.uid, selectedProjectId, selectedFolderId, selectedListId,
        async (viewsData) => {
          if (viewsData.length === 0) {
            await createDefaultViews(user.uid, selectedProjectId, selectedFolderId, selectedListId);
            return; // subscription will fire again with the new views
          }
          setViews(viewsData);
          // If no active view, load user's default or first view
          if (!activeViewId || !viewsData.find(v => v.id === activeViewId)) {
            const defaultId = await getUserDefaultView(user.uid, selectedListId);
            const targetView = defaultId ? viewsData.find(v => v.id === defaultId) : viewsData[0];
            if (targetView) {
              setActiveViewId(targetView.id);
              setActiveView(targetView.type);
              if (targetView.filters && Object.keys(targetView.filters).some(k => targetView.filters[k])) {
                setFilters(prev => ({ ...prev, ...targetView.filters }));
              }
            }
          }
        }
      );
    };
    init();
    return () => { if (unsubRef) unsubRef(); };
  }, [user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  // When activeViewId changes, apply the view's type and filters
  useEffect(() => {
    if (!activeViewId || views.length === 0) return;
    const view = views.find(v => v.id === activeViewId);
    if (view) {
      setActiveView(view.type);
      if (view.filters && Object.keys(view.filters).some(k => view.filters[k])) {
        setFilters({ estado: null, prioridad: null, asignado: null, fechaDesde: null, fechaHasta: null, ...view.filters });
      } else {
        setFilters({ estado: null, prioridad: null, asignado: null, fechaDesde: null, fechaHasta: null });
      }
    }
  }, [activeViewId]);

  // Filtered tasks based on search + filters
  const filteredTasks = useMemo(() => {
    let result = tasks;
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      result = result.filter(t =>
        (t.titulo || '').toLowerCase().includes(q) ||
        (t.descripcion || '').toLowerCase().includes(q) ||
        (t.asignados || []).some(a => a.toLowerCase().includes(q))
      );
    }
    if (filters.estado) result = result.filter(t => t.estado === filters.estado);
    if (filters.prioridad) result = result.filter(t => t.prioridad === filters.prioridad);
    if (filters.asignado) result = result.filter(t => (t.asignados || []).includes(filters.asignado));
    if (filters.fechaDesde) {
      const desde = new Date(filters.fechaDesde);
      result = result.filter(t => {
        if (!t.fechaVencimiento) return false;
        const d = typeof t.fechaVencimiento === 'object' && t.fechaVencimiento.seconds ? new Date(t.fechaVencimiento.seconds * 1000) : new Date(t.fechaVencimiento);
        return d >= desde;
      });
    }
    if (filters.fechaHasta) {
      const hasta = new Date(filters.fechaHasta);
      hasta.setHours(23, 59, 59);
      result = result.filter(t => {
        if (!t.fechaVencimiento) return false;
        const d = typeof t.fechaVencimiento === 'object' && t.fechaVencimiento.seconds ? new Date(t.fechaVencimiento.seconds * 1000) : new Date(t.fechaVencimiento);
        return d <= hasta;
      });
    }
    return result;
  }, [tasks, searchQuery, filters]);

  const hasActiveFilters = searchQuery || filters.estado || filters.prioridad || filters.asignado || filters.fechaDesde || filters.fechaHasta;
  const clearFilters = () => { setSearchQuery(''); setFilters({ estado: null, prioridad: null, asignado: null, fechaDesde: null, fechaHasta: null }); };

  const value = {
    // Proyectos
    projects,
    selectedProjectId,
    setSelectedProjectId,

    // Carpetas
    folders,
    selectedFolderId,
    setSelectedFolderId,
    loadingFolders,
    reloadFolders,

    // Listas
    lists,
    selectedListId,
    setSelectedListId,
    loadingLists,
    reloadLists,

    // Tareas
    tasks,
    selectedTaskId,
    setSelectedTaskId,
    selectedSubtaskPath,
    setSelectedSubtaskPath,

    // Mis Tareas (cross-project)
    myTasks,
    setMyTasks,

    // Secciones
    sections,
    loadingSections,

    // Usuarios
    users,

    // UI
    loading,
    showTaskDrawer,
    setShowTaskDrawer,
    activeView,
    setActiveView,
    views,
    activeViewId,
    setActiveViewId,

    // Search & Filters
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    filteredTasks,
    hasActiveFilters,
    clearFilters,

    // Helpers
    getSelectedProject: () => projects.find(p => p.id === selectedProjectId),
    getSelectedFolder: () => folders.find(f => f.id === selectedFolderId),
    getSelectedList: () => lists.find(l => l.id === selectedListId),
    getSelectedTask: () => tasks.find(t => t.id === selectedTaskId)
  };

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
};
